import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

// German percent label: 41 -> "41 %", 24.44 -> "24,4 %"
const fmtPct = (v) => Number(v).toLocaleString('de-DE', { maximumFractionDigits: 1 }) + ' %'

// Small source credit drawn into every public chart canvas (survives screenshots / re-use).
export const sourceCreditPlugin = {
  id: 'sourceCredit',
  afterDraw(chart) {
    const { ctx, width, height } = chart
    ctx.save()
    ctx.font = '10px sans-serif'
    ctx.fillStyle = 'rgba(107, 114, 128, 0.9)'
    ctx.textAlign = 'right'
    ctx.textBaseline = 'bottom'
    ctx.fillText('Quelle: PflegeHelfer24', width - 6, height - 3)
    ctx.restore()
  }
}

const MOBILE_BREAKPOINT_MAX = 639

const coalesce = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null) return value
  }
  return undefined
}

const isMobileViewport = () =>
  typeof window !== 'undefined' &&
  window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_MAX}px)`).matches

const findVerticalScale = (chart) => {
  const scales = chart.options?.scales

  if (!scales) return null

  const entries = Object.entries(scales)

  for (const [key, scale] of entries) {
    if (!scale) continue
    const axis = scale.axis || key[0]

    if (axis === 'y' || scale.position === 'left') {
      return { key, scale }
    }
  }

  return null
}

const ensurePaddingObject = (chart) => {
  const layout = chart.options.layout || (chart.options.layout = {})
  let padding = layout.padding

  if (padding == null) {
    padding = { top: 0, right: 0, bottom: 0, left: 0 }
  } else if (typeof padding === 'number') {
    padding = { top: padding, right: padding, bottom: padding, left: padding }
  } else {
    padding = {
      top: padding.top != null ? padding.top : 0,
      right: padding.right != null ? padding.right : 0,
      bottom: padding.bottom != null ? padding.bottom : 0,
      left: padding.left != null ? padding.left : 0
    }
  }

  layout.padding = padding
  return padding
}

const resolveYAxisFont = (chart, scale) => {
  const defaults = Chart.defaults?.font || {}
  const titleConfig = scale?.title || {}
  const fontOptions = titleConfig.font || {}

  return {
    size: coalesce(fontOptions.size, defaults.size, 12),
    family: coalesce(fontOptions.family, defaults.family, 'sans-serif'),
    style: coalesce(fontOptions.style, defaults.style, 'normal'),
    weight: coalesce(fontOptions.weight, defaults.weight, '')
  }
}

const legendSpacingPlugin = {
  id: 'legendSpacing',
  afterInit(chart, _args, pluginOptions) {
    const spacing = pluginOptions && pluginOptions.padding != null ? pluginOptions.padding : 0

    if (!spacing) return

    const legend = chart.legend

    if (!legend || legend._legendSpacingApplied) return

    const originalFit = legend.fit

    if (typeof originalFit !== 'function') return

    legend.fit = function fit() {
      originalFit.call(this)
      this.height += spacing
    }

    legend._legendSpacingApplied = true
  }
}

const mobileYAxisTitlePlugin = {
  id: 'mobileYAxisTitle',
  beforeInit(chart) {
    chart.$mobileYAxisTitleState = {
      originalPaddingTop: undefined,
      verticalScaleKey: undefined
    }
  },
  beforeUpdate(chart, _args, pluginOptions) {
    const text = pluginOptions?.text
    const target = findVerticalScale(chart)

    if (!target?.scale || !text) return

    const state = chart.$mobileYAxisTitleState || (chart.$mobileYAxisTitleState = {})
    state.verticalScaleKey = target.key

    const title = target.scale.title || (target.scale.title = {})
    title.display = !isMobileViewport()
  },
  beforeLayout(chart, _args, pluginOptions) {
    const text = pluginOptions?.text
    const state = chart.$mobileYAxisTitleState
    if (!text || !state) return

    const target = findVerticalScale(chart)
    if (!target) return

    const padding = ensurePaddingObject(chart)

    if (state.originalPaddingTop === undefined) {
      state.originalPaddingTop = padding.top != null ? padding.top : 0
    }

    if (isMobileViewport()) {
      const font = resolveYAxisFont(chart, target.scale)
      const requiredPadding = (font.size || 12) + 12
      const baselinePadding = state.originalPaddingTop != null ? state.originalPaddingTop : 0
      padding.top = Math.max(baselinePadding, requiredPadding)
    } else if (state.originalPaddingTop !== undefined) {
      padding.top = state.originalPaddingTop
    }
  },
  afterDraw(chart, _args, pluginOptions) {
    const text = pluginOptions?.text
    if (!text || !isMobileViewport()) return

    const target = findVerticalScale(chart)
    if (!target) return

    const { ctx, chartArea } = chart
    if (!chartArea) return

    const font = resolveYAxisFont(chart, target.scale)
    const color = target.scale?.title?.color || '#374151'

    const fontSegments = [
      font.style && font.style !== 'normal' ? font.style : '',
      font.weight || '',
      `${font.size}px`,
      font.family
    ].filter(Boolean)

    ctx.save()
    ctx.font = fontSegments.join(' ')
    ctx.fillStyle = color
    ctx.textAlign = 'left'
    ctx.textBaseline = 'bottom'
    ctx.fillText(text, chartArea.left, chartArea.top - 4)
    ctx.restore()
  }
}

// Register Chart.js plugins - DO NOT register ChartDataLabels globally
// as it will show labels on all charts. Instead, register it per-chart basis.
Chart.register(...registerables, legendSpacingPlugin, mobileYAxisTitlePlugin)

export default class extends Controller {
  static values = {
    type: String,
    labels: Array,
    datasets: Array,
    colors: Array,
    borderColor: String,
    max: Number,
    title: String,
    options: Object,  // Add support for passing custom options
    lazyLoad: { type: Boolean, default: true }  // Enable lazy loading by default
  }

  connect() {
    this.chartInitialized = false

    if (this.lazyLoadValue) {
      // Set up intersection observer for lazy loading
      this.setupLazyLoading()
    } else {
      // Initialize chart immediately
      this.initializeChart()
    }
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  setupLazyLoading() {
    // Create an intersection observer to watch when the chart comes into view
    const options = {
      root: null, // viewport
      rootMargin: '50px', // Start loading 50px before the chart is visible
      threshold: 0.1 // Trigger when at least 10% of the chart is visible
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.chartInitialized) {
          console.log('Chart coming into view, initializing...')
          this.initializeChart()
          this.chartInitialized = true
          // Stop observing once initialized
          this.observer.unobserve(this.element)
        }
      })
    }, options)

    // Start observing the chart element
    this.observer.observe(this.element)
  }
  
  getDefaultColors() {
    return [
      '#4cd137', '#00aeff', '#9c88ff', '#fbc531', '#7bed9f', 
      '#ff6b81', '#a29bfe', '#ff7f50', '#f0932b', '#eb4d4b'
    ]
  }

  initializeChart() {
    const ctx = this.element.getContext('2d')

    // Destroy existing chart if it exists
    if (this.chart) {
      this.chart.destroy()
    }

    // Apply BNet styling to charts
    const bnetTheme = {
      backgroundColor: '#15202d',
      textColor: '#8ba1bc',
      gridColor: '#2a394f',
      accentColor: '#00aeff',
      positiveColor: '#4cd137',
      warningColor: '#fbc531',
      errorColor: '#e84118'
    }

    // Configure chart based on type
    let config = {}

    if (this.typeValue === 'bar') {
      config = this.getBarChartConfig(bnetTheme)
    } else if (this.typeValue === 'line') {
      config = this.getLineChartConfig(bnetTheme)
    } else if (this.typeValue === 'pie' || this.typeValue === 'doughnut') {
      config = this.getPieChartConfig(bnetTheme)
    }

    // Apply any custom options passed via data-chart-options-value
    if (this.hasOptionsValue) {
      config.options = this.mergeDeep(config.options, this.optionsValue)
    }

    // Register datalabels plugin if it's enabled in the options
    if (this.hasOptionsValue &&
        this.optionsValue.plugins?.datalabels?.display) {
      console.log('Datalabels plugin enabled, config:', this.optionsValue.plugins.datalabels)
      config.plugins = config.plugins || []
      if (!config.plugins.includes(ChartDataLabels)) {
        config.plugins.push(ChartDataLabels)
      }

      // Handle custom formatter functions
      const datalabelsConfig = config.options.plugins?.datalabels
      console.log('Datalabels formatter type:', datalabelsConfig?.formatter)
      if (datalabelsConfig?.formatter === 'valuePercent') {
        // values are already percentages (e.g. 59 -> "59 %"); no recomputation
        datalabelsConfig.formatter = (value) => fmtPct(value)
      } else if (datalabelsConfig?.formatter === 'labelWithValue') {
        datalabelsConfig.formatter = function(value, context) {
          const label = context.dataset.label || ''
          return label + ' (' + value + ')'
        }
      } else if (datalabelsConfig?.formatter === 'percentage') {
        // For bar charts: calculate percentage based on context
        datalabelsConfig.formatter = function(value, context) {
          const dataIndex = context.dataIndex
          const datasets = context.chart.data.datasets
          const isStacked = context.chart.options?.scales?.x?.stacked ||
                           context.chart.options?.scales?.y?.stacked

          let total = 0

          if (isStacked) {
            // For stacked charts: sum all datasets at this index
            total = datasets.reduce((sum, dataset) => {
              return sum + (dataset.data[dataIndex] || 0)
            }, 0)
          } else {
            // For non-stacked charts: sum all values in the current dataset
            total = context.dataset.data.reduce((sum, val) => sum + val, 0)
          }

          return total > 0 ? fmtPct((value / total) * 100) : fmtPct(0)
        }
      } else if (datalabelsConfig?.formatter === 'labelWithPercentage') {
        // For stacked bar charts with labels: show label and percentage
        datalabelsConfig.formatter = function(value, context) {
          const label = context.dataset.label || ''
          const dataIndex = context.dataIndex
          const datasets = context.chart.data.datasets
          const isStacked = context.chart.options?.scales?.x?.stacked ||
                           context.chart.options?.scales?.y?.stacked

          let total = 0

          if (isStacked) {
            // For stacked charts: sum all datasets at this index
            total = datasets.reduce((sum, dataset) => {
              return sum + (dataset.data[dataIndex] || 0)
            }, 0)
          } else {
            // For non-stacked charts: sum all values in the current dataset
            total = context.dataset.data.reduce((sum, val) => sum + val, 0)
          }

          return label + '\n' + (total > 0 ? fmtPct((value / total) * 100) : fmtPct(0))
        }
      } else if (datalabelsConfig?.formatter === 'piePercentage') {
        datalabelsConfig.formatter = function(value, context) {
          const dataset = context.dataset.data
          const total = dataset.reduce((acc, val) => acc + val, 0)
          return fmtPct((value / total) * 100)
        }
      } else if (datalabelsConfig?.formatter === 'pieLabelWithPercentage') {
        datalabelsConfig.formatter = function(value, context) {
          const label = context.chart.data.labels[context.dataIndex]
          const dataset = context.dataset.data
          const total = dataset.reduce((acc, val) => acc + val, 0)
          return label + '\n' + fmtPct((value / total) * 100)
        }
      }
    }

    const tooltipCallbacks = config.options.plugins?.tooltip?.callbacks
    if (tooltipCallbacks?.label === 'percentage') {
      tooltipCallbacks.label = function(context) {
        const dataIndex = context.dataIndex
        const datasets = context.chart.data.datasets
        const isStacked = context.chart.options?.scales?.x?.stacked ||
                          context.chart.options?.scales?.y?.stacked

        let total = 0

        if (isStacked) {
          total = datasets.reduce((sum, dataset) => {
            return sum + (dataset.data[dataIndex] || 0)
          }, 0)
        } else {
          total = context.dataset.data.reduce((sum, val) => sum + val, 0)
        }

        const value = context.dataset.data[dataIndex] || 0
        const label = context.dataset.label ? context.dataset.label + ': ' : ''

        return `${label}${total > 0 ? fmtPct((value / total) * 100) : fmtPct(0)}`
      }
    }

    config.options.locale = config.options.locale || 'de-DE'
    config.options.layout = config.options.layout || {}
    config.options.layout.padding = Object.assign({ bottom: 0 }, config.options.layout.padding || {})
    config.options.layout.padding.bottom = (config.options.layout.padding.bottom || 0) + 12
    config.plugins = [...(config.plugins || []), sourceCreditPlugin]
    this.chart = new Chart(ctx, config)
  }
  
  // Helper to deep merge objects
  mergeDeep(target, source) {
    const isObject = obj => obj && typeof obj === 'object'
    
    if (!isObject(target) || !isObject(source)) {
      return source
    }
    
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} })
        this.mergeDeep(target[key], source[key])
      } else {
        Object.assign(target, { [key]: source[key] })
      }
    })
    
    return target
  }
  
  getBarChartConfig(theme) {
    // Check if we have a mixed chart with different types
    const hasMixedTypes = this.datasetsValue.some(dataset => dataset.type !== undefined);
    
    // Process datasets, preserving all original properties including yAxisID
    const processedDatasets = this.datasetsValue.map((dataset, index) => {
      // Clone the dataset to avoid modifying the original
      const processedDataset = { ...dataset };
      
      // Set default background/border colors if needed
      if (!processedDataset.backgroundColor) {
        processedDataset.backgroundColor = this.colorsValue ? 
          this.colorsValue[index] : theme.accentColor;
      }
      
      if (processedDataset.type === 'line' && !processedDataset.borderColor) {
        processedDataset.borderColor = this.colorsValue ? 
          this.colorsValue[index] : theme.accentColor;
      }
      
      // Set sensible defaults for line charts
      if (processedDataset.type === 'line') {
        if (processedDataset.fill === undefined) processedDataset.fill = false;
        if (processedDataset.borderWidth === undefined) processedDataset.borderWidth = 2;
        if (processedDataset.pointRadius === undefined) processedDataset.pointRadius = 3;
        if (processedDataset.pointHoverRadius === undefined) processedDataset.pointHoverRadius = 5;
        if (processedDataset.tension === undefined) processedDataset.tension = 0.4;
      }
      
      return processedDataset;
    });

    return {
      type: hasMixedTypes ? 'bar' : this.typeValue, // Use 'bar' as base type for mixed charts
      data: {
        labels: this.labelsValue,
        datasets: processedDatasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: this.hasTitleValue,
            text: this.titleValue,
            color: theme.textColor,
            font: {
              size: 16,
              weight: 'bold'
            },
            padding: {
              top: 10,
              bottom: 20
            }
          },
          legend: {
            labels: {
              color: theme.textColor,
              usePointStyle: true,
              padding: 20
            }
          },
          tooltip: {
            backgroundColor: 'rgba(21, 32, 45, 0.9)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: '#3a4a64',
            borderWidth: 1,
            usePointStyle: true,
            boxPadding: 6
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: theme.gridColor,
              drawBorder: false
            },
            ticks: {
              color: theme.textColor
            }
          },
          x: {
            grid: {
              display: false
            },
            ticks: {
              color: theme.textColor
            }
          }
        }
      }
    }
  }

  getLineChartConfig(theme) {
    return {
      type: 'line',
      data: {
        labels: this.labelsValue,
        datasets: this.datasetsValue
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              color: theme.textColor,
              usePointStyle: true,
              padding: 15,
              font: {
                size: 14
              }
            }
          },
          tooltip: {
            backgroundColor: 'rgba(21, 32, 45, 0.9)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: '#3a4a64',
            borderWidth: 1,
            usePointStyle: true,
            boxPadding: 6
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: theme.gridColor,
              drawBorder: false
            },
            ticks: {
              color: theme.textColor
            }
          },
          x: {
            grid: {
              color: 'rgba(42, 57, 79, 0.3)',
              drawBorder: false
            },
            ticks: {
              color: theme.textColor
            }
          }
        }
      }
    }
  }

  getPieChartConfig(theme) {
    return {
      type: this.typeValue,
      data: {
        labels: this.labelsValue,
        datasets: this.datasetsValue
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              color: theme.textColor,
              usePointStyle: true,
              padding: 15,
              font: {
                size: 12
              }
            }
          },
          tooltip: {
            backgroundColor: 'rgba(21, 32, 45, 0.9)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: '#3a4a64',
            borderWidth: 1,
            usePointStyle: true,
            boxPadding: 6,
            callbacks: {
              label: function(context) {
                const label = context.label || '';
                const value = context.parsed || 0;
                const dataset = context.dataset;
                const total = dataset.data.reduce((acc, data) => acc + data, 0);
                const percentage = ((value / total) * 100).toLocaleString('de-DE', { maximumFractionDigits: 1 });
                return `${label}: ${value} (${percentage} %)`;
              }
            }
          }
        }
      }
    }
  }
}
